import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AUTH_CONFIG, type AuthConfig } from '@olv/ui-shared/features/auth';
import {
  AcknowledgeIncompleteRiskDto,
  ApproveTransactionRequestDto,
  CancelTransactionDto,
  PrepareSystemTransactionRequestDto,
  RejectTransactionRequestDto,
  RetryTransactionDto,
  ReverseTransactionDto,
  SetTransactionRiskRequestDto,
  TradeTypeEnum,
} from '../models/merchant-transaction.model';

export interface RecordExchangeDto {
  tradeType: TradeTypeEnum;
  date: string;
  amount: number;
  rate: number;
  fees: number;
  totp: string;
}

export type EditExchangeDto = Partial<Omit<RecordExchangeDto, 'totp'>> & Pick<RecordExchangeDto, 'totp'>;

export interface DeleteExchangeDto {
  totp: string;
}

export interface SetExchangeRateDto {
  exchangeRateToBase: number;
  linkedTransactionIds?: string[];
  comment?: string;
  totp: string;
}

export interface SetPendingExchangeRatesDto {
  exchangeRateToBase: number;
  comment?: string;
  totp: string;
}

export interface PendingExchangeRateReleaseResult {
  released: number;
  alreadyReleased: number;
  outstanding: number;
  failed: { transactionId: string; reason: string }[];
}

@Injectable({
  providedIn: 'root',
})
export class SystemTransactionService {
  private http = inject(HttpClient);

  private readonly cfg = inject<AuthConfig>(AUTH_CONFIG);

  private baseUrl = `${this.cfg.baseUrl}/api/SystemTransaction`;

  /**
   * Sends a prepare transaction request to the backend
   * @param prepareTransactionRequest The transaction details to prepare
   * @returns Observable with the transaction ID in the response
   */
  prepareTransaction(prepareTransactionRequest: PrepareSystemTransactionRequestDto): Observable<any> {
    const url = `${this.baseUrl}/prepare`;
    return this.http.post<any>(url, prepareTransactionRequest);
  }

  /**
   * Gets the status of a transaction
   * @param transactionId The ID of the transaction to check
   * @returns Observable with transaction details
   */
  getTransactionStatus(transactionId: string): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/status`;
    return this.http.get<any>(url);
  }

  /**
   * Approves a pending transaction
   * @param transactionId The ID of the transaction to approve
   * @param approveRequest The approval details including comments
   * @returns Observable of the response
   */
  approveTransaction(transactionId: string, approveRequest: ApproveTransactionRequestDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/approve`;
    return this.http.post<any>(url, approveRequest);
  }

  /**
   * Reject a pending transaction
   * @param transactionId The ID of the transaction to approve
   * @param rejectRequest The approval details including comments
   * @returns Observable of the response
   */
  rejectTransaction(transactionId: string, rejectRequest: RejectTransactionRequestDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/reject`;
    return this.http.post<any>(url, rejectRequest);
  }

  approveDeposit(transactionId: string, approveRequest: ApproveTransactionRequestDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/approve-deposit`;
    return this.http.post<any>(url, approveRequest);
  }

  rejectDeposit(transactionId: string, rejectRequest: RejectTransactionRequestDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/reject-deposit`;
    return this.http.post<any>(url, rejectRequest);
  }

  reverseDeposit(transactionId: string, reverseRequest: ReverseTransactionDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/reverse-deposit`;
    return this.http.post<any>(url, reverseRequest);
  }

  cancelTransaction(transactionId: string, request: CancelTransactionDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/cancel`;
    return this.http.post<any>(url, request);
  }

  cancelPreparedTransaction(transactionId: string, rejectRequest: RejectTransactionRequestDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/cancel-prepared`;
    return this.http.post<any>(url, rejectRequest);
  }

  speedupTransaction(transactionId: string, request: CancelTransactionDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/speed-up`;
    return this.http.post<any>(url, request);
  }

  retryTransaction(transactionId: string, retryRequest: RetryTransactionDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/retry`;
    return this.http.post<any>(url, retryRequest);
  }

  recordExchange(transactionId: string, recordRequest: RecordExchangeDto): Observable<any> {
    const url = `${this.baseUrl}/exchange/${transactionId}`;
    return this.http.post<any>(url, recordRequest);
  }

  editExchange(transactionId: string, editRequest: EditExchangeDto): Observable<any> {
    const url = `${this.baseUrl}/exchange/${transactionId}`;
    return this.http.put<any>(url, editRequest);
  }

  deleteExchange(transactionId: string, deleteRequest: DeleteExchangeDto): Observable<any> {
    const url = `${this.baseUrl}/exchange/${transactionId}`;
    return this.http.delete<any>(url, { body: deleteRequest });
  }

  setRisk(transactionId: string, request: SetTransactionRiskRequestDto): Observable<void> {
    const url = `${this.baseUrl}/${transactionId}/set-risk`;
    return this.http.post<void>(url, request);
  }

  acknowledgeIncompleteRisk(
    transactionId: string,
    taskId: string,
    request: AcknowledgeIncompleteRiskDto,
  ): Observable<void> {
    const url = `${this.baseUrl}/${transactionId}/acknowledge-incomplete-risk/${taskId}`;
    return this.http.post<void>(url, request);
  }

  setExchangeRate(transactionId: string, request: SetExchangeRateDto): Observable<void> {
    const url = `${this.baseUrl}/${transactionId}/exchange-rate`;
    return this.http.post<void>(url, request);
  }

  setExchangeRateForPending(
    transactionId: string,
    request: SetPendingExchangeRatesDto,
  ): Observable<PendingExchangeRateReleaseResult> {
    const url = `${this.baseUrl}/${transactionId}/exchange-rate/pending`;
    return this.http.post<PendingExchangeRateReleaseResult>(url, request);
  }
}
